import React, { useState } from 'react';
import { Form, Modal, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../hooks/defaultHooks';
import { closeModal } from '../../../store/slices/modalSlice';
import { modalExtra } from '../../../selectors/selectors';
import { isFetchBaseQueryError } from '../../../utils/helpers';
import { useRejectReferralRewardMutation } from '../../referralRewards/referralRewards.service';
import cls from './RejectReferralReward.module.scss';

/** The reason is kept with the reward, so the column apisupport writes it to bounds it */
const REASON_MAX_LENGTH = 255;

const errorText = (error: unknown) => {
  if (!isFetchBaseQueryError(error)) return 'Oops. Something went wrong';
  if (error.status === 403) return 'Only a superadmin decides referral rewards';
  if (error.status === 404) return 'The reward is not found';
  if (error.status === 409) return 'The reward is already decided';
  return 'Oops. Something went wrong';
};

/** Rejects a pending referral reward: nobody is credited, and the reward is used up all the same */
const RejectReferralReward = () => {
  const extra = useAppSelector(modalExtra);
  const dispatch = useAppDispatch();
  const [rejectReward] = useRejectReferralRewardMutation();
  const [disabled, setDisabled] = useState(false);
  const closeHandler = () => dispatch(closeModal());
  const formik = useFormik({
    initialValues: { reason: '' },
    validationSchema: yup.object().shape({
      reason: yup
        .string()
        .trim()
        .max(REASON_MAX_LENGTH, `At most ${REASON_MAX_LENGTH} characters`)
        .required('This field is required'),
    }),
    onSubmit: async ({ reason }) => {
      setDisabled(true);
      try {
        await rejectReward({ inviteeId: extra?.id as string, reason: reason.trim() }).unwrap();
        toast.success('Referral reward rejected');
        dispatch(closeModal());
      } catch (error) {
        toast.error(errorText(error));
      } finally {
        setDisabled(false);
      }
    },
  });
  return (
    <>
      <Modal.Header className={cls.ModalHeader} closeButton>
        <h3 className="title-3" data-testid="modal-title">
          Reject referral reward?
        </h3>
      </Modal.Header>
      <Modal.Body className={cls.ModalBody}>
        <p>
          The reward for <strong className={cls.TextInformation}>{extra?.textInformation}</strong>{' '}
          will credit nobody and cannot be granted later
        </p>
        <Form onSubmit={formik.handleSubmit} className={cls.Form}>
          <Form.Group className="position-relative">
            <Form.Label htmlFor="reason">Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formik.values.reason}
              placeholder="Why the reward is rejected"
              onChange={formik.handleChange}
              name="reason"
              id="reason"
              isInvalid={!!formik.errors.reason}
            />
            <Form.Control.Feedback type="invalid" tooltip>
              {formik.errors.reason}
            </Form.Control.Feedback>
          </Form.Group>
          <div className={cls.Buttons}>
            <Button
              type="submit"
              className={cls.Button}
              disabled={disabled || !formik.values.reason.trim()}
            >
              Reject
            </Button>
            <Button onClick={closeHandler} className={cls.CancelButton}>
              Cancel
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </>
  );
};

export default RejectReferralReward;
